import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  ChatStates,
  selectChatState,
  virtualHumanTalkingSelector,
} from '../../../../slices/chat/ChatSlice';
import RecordingButton from '../../RecordingButton/RecordingButton';
import * as Styled from './DeepgramButtonPushToTalk.styled';
import { languageSelector } from '../../../../slices/PatientSlice';
import useDeepgramDirect from '../../../../utility/useDeepgramDirect';
import { MicMutedContext } from '../../MicMutedProvider.context';
import { MicrophoneStreamContext } from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import { useUpdateMicActive } from '@vh/components/VHChatInputArea/VHTextChatInput/useUpdateMicActive';
import { usePrevious } from '@base/utility/CustomHooks';
import { useDisconnectNotify } from '@vh/utility/useDisconnectNotify';

/**
 * @type {import('react').FC<{
 *  onSubmit: (chat: string) => void;
 *  onChange: (e: string) => void;
 *  textDisabled: boolean;
 *  recording: boolean;
 *  onRecording: (newRecording: boolean) => void;
 *  recordingDisabled: boolean;
 *  onSpeechError: (err: string) => void;
 *  currentChat: string;
 *  onPendingTranscription: (pending: boolean) => void;
 *  onPreviewChatChange: (newVal: string) => void;
 *  design: "fill" | "outline" | "transparent" | "text";
 *  appendPreviewChat: string;
 * }>}
 */

const DeepgramButtonPushToTalk = ({
  onSubmit,
  onChange,
  textDisabled,
  recordingDisabled,
  onSpeechError,
  currentChat,
  onPendingTranscription,
  design,
  onPreviewChatChange,
  appendPreviewChat,
  recording,
  onRecording,
}) => {
  const { muted, setMuted } = useContext(MicMutedContext);
  const { setVolume } = useContext(MicrophoneStreamContext);
  const chatState = useSelector(selectChatState);
  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  const language = useSelector(languageSelector);
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  const showMutedNoticeIfTalked =
    muted &&
    !virtualHumanTalking &&
    chatLoadingStatus !== 'pending' &&
    chatState !== ChatStates.LOADING;
  const pauseDeepgram =
    muted ||
    textDisabled ||
    virtualHumanTalking ||
    chatLoadingStatus === 'pending' ||
    chatState === ChatStates.LOADING ||
    !recording;

  const currentChatRef = useRef('');
  currentChatRef.current = currentChat;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onPreviewChatChangeRef = useRef(onPreviewChatChange);
  onPreviewChatChangeRef.current = onPreviewChatChange;

  const appendPreviewChatRef = useRef(appendPreviewChat);
  appendPreviewChatRef.current = appendPreviewChat;
  const { incrementDisconnects } = useDisconnectNotify();

  const handleCloseSocket = useCallback(
    (closeCode, closeReason) => {
      setMuted(true);
      onRecording(false);
      onPendingTranscription(false);
      // ensure any text up till now is saved so that the unfinished affirmation isn't overwritten
      const hithertoText = currentChatRef.current
        ? `${currentChatRef.current.trim()} ${appendPreviewChatRef.current.trim()}`
        : `${appendPreviewChatRef.current.trim()}`;
      onChangeRef.current(hithertoText);
      onPreviewChatChangeRef.current('');
      if (closeCode !== 1000 && closeReason) {
        incrementDisconnects(closeReason);
      }
    },
    [setMuted, onRecording, onPendingTranscription, incrementDisconnects]
  );

  // use debounced variable so that it doesn't flick between paused and unpaused in the moment that they change
  // const [debouncedPauseDeepgram] = useDebounce(pauseDeepgram, 50);
  const {
    currentUnfinishedAffirmation,
    currentAffirmation,
    finalAffirmation,
    error,
    connected,
    hasAttemptedConnection,
    reconnect,
    currentTranscript,
    resetTranscript,
    connecting,
  } = useDeepgramDirect({
    language,
    paused: pauseDeepgram,
    onClose: handleCloseSocket,
  });

  const prevRecordingDisabled = usePrevious(recordingDisabled);
  useEffect(() => {
    if (!recordingDisabled && prevRecordingDisabled) {
      console.log('resetTranscript');
      resetTranscript();
    }
  }, [recordingDisabled, prevRecordingDisabled, resetTranscript]);

  useEffect(() => {
    setVolume(pauseDeepgram ? 0 : 1);
    console.log('set volume');
  }, [setVolume, pauseDeepgram]);

  const prevFinalAffirmation = usePrevious(finalAffirmation);
  useEffect(() => {
    if (finalAffirmation !== prevFinalAffirmation && !pauseDeepgram && !muted) {
      const submissionText = !currentChat
        ? finalAffirmation
        : `${currentChat} ${finalAffirmation}`;
      onPendingTranscription(false);
      onChange(submissionText);
    }
  }, [
    currentChat,
    finalAffirmation,
    onChange,
    prevFinalAffirmation,
    pauseDeepgram,
    resetTranscript,
    muted,
    onPendingTranscription,
    onPreviewChatChange,
  ]);

  useEffect(() => {
    if (pauseDeepgram) {
      onRecording(false);
    }
  }, [onRecording, pauseDeepgram]);

  useEffect(() => {
    if (currentAffirmation || currentUnfinishedAffirmation) {
      onPendingTranscription(true);
    }
  }, [
    currentAffirmation,
    currentUnfinishedAffirmation,
    onPendingTranscription,
  ]);

  const handleRecordingButtonClick = useCallback(() => {
    if (recording) {
      setVolume(0);
      setMuted(true);
    } else {
      setMuted(false);
      setVolume(1);
    }
    onPendingTranscription(false);
    onRecording(!recording);
  }, [onRecording, setVolume, setMuted, onPendingTranscription, recording]);

  useEffect(() => {
    if (error) {
      onSpeechError(error);
    }
  }, [error, onSpeechError]);

  useEffect(() => {
    if (currentTranscript && recording) {
      onPreviewChatChange(currentTranscript);
    }
  }, [currentTranscript, onPreviewChatChange, recording]);

  useUpdateMicActive(recording);

  return (
    <RecordingButton
      disabled={recordingDisabled}
      onClick={handleRecordingButtonClick}
      muted={muted}
      connected={connected}
      showMutedNoticeIfTalked={showMutedNoticeIfTalked}
      networkError={!connected && error}
      recording={recording}
      connecting={connecting}
      design={design}
    />
  );
};

export default DeepgramButtonPushToTalk;
